import { Component, OnDestroy, OnInit, ViewChild } from '@angular/core';
import { MatPaginator } from '@angular/material/paginator';
import { MatSort } from '@angular/material/sort';
import { Router } from '@angular/router';
import { authSelector } from '@core/services/auth/state/authentication.selectors';
import { select, Store } from '@ngrx/store';
import { SearchFilter, SearchModel } from '@shared/models/search.model';
import { ROUTE_ANIMATIONS_ELEMENTS } from '@shared/services/animations/route.animations';
import { BehaviorSubject, Observable, of, Subscription } from 'rxjs';
import { debounceTime, distinctUntilChanged, map, startWith, switchMap } from 'rxjs/operators';

import { ProtocolModel } from '../models/protocol.model';
import { protocolSelector } from '../store/protocol.selectors';
import { AppState } from './../../../core/app-state/reducers';
import { protocolSearch } from './../store/protocol.actions';
import { OptionModel } from '@features/admin/option-management/models/option.model';
import { optionSelector } from '@features/admin/option-management/store/option.selectors';
import { OptionType } from '@features/admin/option-management/models/option-type.enum';
import { ProtocolService } from '../services/protocol.service';
import { Researcher } from '@features/admin/researcher-management/models/researcher.model';
import { ResearcherService } from '@features/admin/researcher-management/services/researcher.service';
import { displayResearcher as displayResearcherUtil,formatResearcher as formatResearcherUtil, formatPhone as formatPhoneUtil} from '@shared/researcher-format.util';

@Component({
  selector: 'app-protocols-list',
  templateUrl: './protocols-list.component.html',
  styleUrls: ['./protocols-list.component.scss'],
})
export class ProtocolsListComponent implements OnInit, OnDestroy {
  @ViewChild(MatPaginator, { static: true }) paginator!: MatPaginator;
  @ViewChild(MatSort) sort!: MatSort;
  routeAnimationsElements = ROUTE_ANIMATIONS_ELEMENTS;
  headElements = ['irbNumber', 'investigator', 'studyTitle', 'status', 'approvalThrough', 'actions'];
  resultsLength = 0;
  pageSize = 10;
  pageSizeOptions: number[] = [10, 50, 100];

  data$!: Observable<ProtocolModel[]>;
  loadingProtocols$ = new BehaviorSubject<boolean>(false);
  loading$ = this.loadingProtocols$.asObservable();
  subs$: Subscription[] = [];
  isAdmin$ = this.store.pipe(select(authSelector.isAdmin));

  //filter = '';
  protocolStatus!: OptionModel[];
  filteredProtocolStatus$!: Observable<OptionModel[]>;
  investigators!: OptionModel[];
  filteredPrincipalInvestigators$!: Observable<Researcher[]>;
  typeOfReview!: OptionModel[];
  filteredTypeOfReview$!: Observable<OptionModel[]>;
  ntrIrbCoordinators!: OptionModel[];
  filteredNtrIrbCoordinators$!: Observable<Researcher[]>;
  public displayResearcher = (r: Researcher | null) => displayResearcherUtil(r);
  public formatResearcher = (r: Researcher) => formatResearcherUtil(r);
  public formatPhone = (p: string) => formatPhoneUtil(p);
  constructor(
    private store: Store<AppState>,
    public protocolService: ProtocolService,
    public researcherService: ResearcherService,
    private router: Router) { }

  ngOnInit(): void {
    this.setFormOptions();
    this.data$ = this.store.pipe(select(authSelector.accessToken)).pipe(switchMap(token => {
      if (token != null && token !== '') {
        this.insert(this.protocolService.protocolFilterForm.value);
        const filters = new SearchModel();
        filters.order = this.sort?.direction || 'desc';
        filters.sort = this.sort?.active || 'irbNumber';
        filters.pageSize = this.protocolService?.pageSize;
        filters.pageIndex = this.protocolService?.pageIndex;
        filters.filter = { ...this.protocolService.searchFilterModel } as SearchFilter;     
        return this.getProtocols(filters);
      }
      return of([]);
    }));
  }

  onSubmit(): void {
    this.insert(this.protocolService.protocolFilterForm.value);
    this.protocolService.pageIndex = 0
    this.loadData();
  }

  loadData(){
    const filters = new SearchModel();
    filters.order = this.sort?.direction || 'desc';
    filters.sort = this.sort?.active || 'irbNumber';  
    filters.pageSize = this.protocolService?.pageSize
    filters.pageIndex = this.protocolService?.pageIndex; 
    filters.filter = { ...this.protocolService.searchFilterModel } as SearchFilter;
    return this.getProtocols(filters);
  }

  view(protocolId: number): void {
    this.router.navigate(['/protocol', protocolId]);
  }

  sortData(): void {
    this.protocolService.pageIndex = this.paginator.pageIndex = 0;//reset index to 0 
    this.loadData();
  }

  onPageChange(event: any) {
    this.protocolService.pageSize  = event.pageSize;
    this.protocolService.pageIndex = event.pageIndex;
    this.pageSize = event.pageSize; // Update the central pageSize
    this.loadData();
  }

  ngOnDestroy(): void {
    this.subs$.forEach(sub => sub.unsubscribe());
  }

  private getProtocols(filters: SearchModel): Observable<ProtocolModel[]> {
    this.loadingProtocols$.next(true);
    this.store.dispatch(protocolSearch({ options: filters }));
    return this.store.pipe(select(protocolSelector.allProtocols)).pipe(map(x => {
      this.resultsLength = x?.count;
      this.loadingProtocols$.next(false);
      return x?.protocols;
    }));
  }


  resetFilter(): void {
    this.protocolService.protocolFilterForm.reset();
    this.insert(this.protocolService.protocolFilterForm.value);
    this.paginator.pageIndex = 0;  // If the user applies filter, reset back to the first page.
    this.sortData();

  }

  private insert(data: any): void {
    if (data != null) {
      if (data.studyTitle != null) {
        this.protocolService.searchFilterModel.studyTitle = data.studyTitle;
      }
      else {
        this.protocolService.searchFilterModel.studyTitle = '';
      }
      if (data.ntrIrb_CoordinatorId != null && data.ntrIrb_CoordinatorId !== undefined && data.ntrIrb_CoordinatorId !== 0 && data.ntrIrb_CoordinatorId !== '') {
        this.protocolService.searchFilterModel.ntrIrb_CoordinatorId = data.ntrIrb_CoordinatorId.id;
      }
      else {
        this.protocolService.searchFilterModel.ntrIrb_CoordinatorId = undefined;
      }

      if (data.principalInvestigatorId != null && data.principalInvestigatorId !== undefined && data.principalInvestigatorId !== 0 && data.principalInvestigatorId !== '') {
        this.protocolService.searchFilterModel.principalInvestigatorId = data.principalInvestigatorId.id;
      }
      else {
        this.protocolService.searchFilterModel.principalInvestigatorId = undefined;
      }


      if (data.protocolStatusId != null && data.protocolStatusId !== undefined && data.protocolStatusId !== 0 && data.protocolStatusId !== '') {

        this.protocolService.searchFilterModel.protocolStatusId = data.protocolStatusId.id;
      }
      else {
        this.protocolService.searchFilterModel.protocolStatusId = undefined;
      }

      if (data.approvalThrough != null) {
        this.protocolService.searchFilterModel.approvalThrough = data.approvalThrough.toLocaleString();
      }
      else {
        this.protocolService.searchFilterModel.approvalThrough = '';
      }

      if (data.irbNumber != null) {
        this.protocolService.searchFilterModel.irbNumber = data.irbNumber;
      }
      else {
        this.protocolService.searchFilterModel.irbNumber = '';
      }
      if (data.irbNetNumber != null) {
        this.protocolService.searchFilterModel.irbNetNumber = data.irbNetNumber;
      }
      else {
        this.protocolService.searchFilterModel.irbNetNumber = '';
      }
      if (data.sendNextReview != null) {
        this.protocolService.searchFilterModel.sendNextReview = data.sendNextReview.toLocaleString();
      }
      else {
        this.protocolService.searchFilterModel.sendNextReview = '';
      }
    }
  }

  private setFormOptions(): void {
      this.filteredPrincipalInvestigators$ = this.protocolService.protocolFilterForm.controls.principalInvestigatorId?.valueChanges.pipe(
        startWith(''),
        debounceTime(300),
          distinctUntilChanged(),
          switchMap(value => {
            if (typeof value === 'string') {
              // Include both active and inactive Principal Investigators
              return this.researcherService.searchResearchers(value, 'PrincipalInvestigator');
            }
             return of([]);
    }));
    this.filteredNtrIrbCoordinators$ = this.protocolService.protocolFilterForm.controls.ntrIrb_CoordinatorId?.valueChanges.pipe(
        startWith(''),
        debounceTime(300),
          distinctUntilChanged(),
          switchMap(value => {
            if (typeof value === 'string') {
              // Include both active and inactive Coordinators
              return this.researcherService.searchResearchers(value, 'NTRIRBCoordinator');
            }
             return of([]);
    }));


    this.subs$.push(this.store.pipe(select(optionSelector.optionsByType(OptionType.protocolStatus))).subscribe((options: OptionModel[]) => {
      this.protocolStatus = options;
      this.filteredProtocolStatus$ = this.protocolService.protocolFilterForm.controls.protocolStatusId?.valueChanges.pipe(
        startWith(''),
        map(value => typeof value === 'string' ? value : value?.name),
        map(name => name ? this._filter(name, this.protocolStatus) : this.protocolStatus.slice()));
    }));
  }
  private _filter(name: string, optionList: OptionModel[]): any {
    const filterValue = name.trim().toLowerCase();
    return optionList.filter(option => option.name.trim().toLowerCase().includes(filterValue));
  }

  displayFn(option: OptionModel): string {
    return option && option.name ? option.name : '';
  }

  getPIRole(element: any) {
    return element.principalInvestigator?.researcherRoles?.find((r:any) => r.role === 'PrincipalInvestigator') ?? null;
  }

  onDateChange(dateString: any, formControl: string) {
    if (dateString != null) {
      this.protocolService.protocolFilterForm.controls[formControl].setValue(new Date(dateString.target.value.substring(0, 2) + "/" + dateString.target.value.substring(2, 4) + "/" +
        dateString.target.value.substring(4, dateString.length)));
    }
  }

}
